import React, { useLayoutEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import './AchievementsSection.css';

// --- Image & Logo Imports ---
import Achievement1 from "../Images/acheivement1BG.jpg";
import Achievement2 from "../Images/acheivement2BG.jpg";
import Achievement3 from "../Images/acheivement3BG.jpg";
import Achievement4 from "../Images/Light1.jpg";
import Achievement5 from "../Images/acheivement4BG.jpg";
import Achievement6 from "../Images/acheivement5BG.jpg";
import client1logo from "../Images/1 Client.png";
import client2logo from "../Images/2 Client.png";
import client3logo from "../Images/3 Client.png";
import client4logo from "../Images/4 Client.png";
import client5logo from "../Images/5 Client.png";
import client6logo from "../Images/6 Client.png";

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

const achievementData = [
  {
    id: 'expertise',
    title: '18 Years of Expertise',
    description: 'Successfully operating for almost two decades, Smartlight has established a rich legacy of innovation and quality in lighting industry.',
    imageUrl: Achievement1,
    logoUrl: client6logo,
    linkUrl: "https://www.goodhomes.co.in/home-decor/home-tours/this-house-in-bengaluru-is-a-modern-home-rooted-in-tradition-9337.html"
  },
  {
    id: 'customization',
    title: 'Made-In-India Customization',
    description: 'We offer handcrafted, fully customizable lighting—including dimensions and color temperatures—demonstrating strong manufacturing capabilities rooted in Surat.',
    imageUrl: Achievement2,
    logoUrl: client5logo,
    linkUrl: "https://www.designpataki.com/a-serene-penthouse-in-chennai-is-imbued-with-japanese-aesthetics-of-wabi-sabi-and-shibui/"
  },
  {
    id: 'one-stop',
    title: 'One-Stop Architectural-Studio',
    description: 'Providing an extensive range of technical and design-focused lighting products, paired with expert consulting, making us a comprehensive project provider.',
    imageUrl: Achievement3,
    logoUrl: client4logo,
    linkUrl: "https://elledecor.in/greige-studio-designs-kochi-home-in-minimal-modern/"
  },
  {
    id: 'support',
    title: 'Always by Your Side',
    description: 'Dedicated to user satisfaction, Smartlight offers responsive help, proactive solutions, and lasting trust with every purchase.',
    imageUrl: Achievement4,
    logoUrl: client3logo,
    linkUrl: "https://volzero.com/articles/view/bonty-ketak-by-sharan-architecture-design"
  },
  {
    id: 'testimonials',
    title: 'Impressive Client Testimonials',
    description: 'Customers and industry partners consistently applaud our technical expertise, value-for-money, and outstanding service quality.',
    imageUrl: Achievement5,
    logoUrl: client2logo,
    linkUrl: "https://www.architectandinteriorsindia.com/projects/break-the-mold-with-dynamic-design-at-this-1800-sq-ft-concrete-showroom-in-mumbai"
  },
  {
    id: 'recognition',
    title: 'Recognized Among Top Smart-Lighting Companies',
    description: 'Featured by B2B platforms as a standout smart lighting company, emphasizing our focus on innovation and technology-driven solutions.',
    imageUrl: Achievement6,
    logoUrl: client1logo,
    linkUrl: "https://thearchitectsdiary.com/interior-design-of-office-space-blends-operations-with-an-immersive-showcase-of-craft-flow-studio/"
  },
];

const AchievementsSection = () => {
  const sectionRef = useRef(null);
  const pinWrapperRef = useRef(null);
  const titleRef = useRef(null);
  const indicatorRef = useRef(null);
  const panelsRef = useRef([]);
  panelsRef.current = [];

  const addToRefs = (el) => {
    if (el && !panelsRef.current.includes(el)) panelsRef.current.push(el);
  };

  useLayoutEffect(() => {
    const panels = panelsRef.current;

    const ctx = gsap.context(() => {
      ScrollTrigger.matchMedia({

        // --- DESKTOP ANIMATION ---
        "(min-width: 769px)": function () {
          gsap.set(panels, { flex: 0.5 });
          gsap.set(panels.map(p => p.querySelector('.panel-logo')), { opacity: 0, y: 15 });
          gsap.set(panels.map(p => p.querySelector('.text-content-wrapper')), { opacity: 0, y: 25 });

          if (panels[0]) {
            gsap.set(panels[0], { flex: 5 });
            gsap.set(panels[0].querySelector('.panel-logo'), { opacity: 1, y: 0 });
            gsap.set(panels[0].querySelector('.text-content-wrapper'), { opacity: 1, y: 0 });
          }

          // Pinning setup
          const panelsTimeline = gsap.timeline({
            scrollTrigger: {
              trigger: pinWrapperRef.current,
              pin: true,
              scrub: 1.5,
              start: 'top top',
              end: () => `+=${window.innerHeight * 2}`,
              pinSpacing: true,
              invalidateOnRefresh: true,
              fastScrollEnd: true,
            },
          });

          // Animation sequence
          panels.forEach((panel, i) => {
            if (i === 0) return;

            panelsTimeline
              .addLabel(`section-${i}`)
              .to(panels[i - 1], { flex: 0.5, duration: 1 }, `section-${i}`)
              .to(panels[i - 1].querySelector('.panel-logo'), { opacity: 0, y: -10, duration: 0.3 }, `section-${i}`)
              .to(panels[i - 1].querySelector('.text-content-wrapper'), { opacity: 0, y: -15, duration: 0.3 }, `section-${i}`)

              .to(panel, { flex: 5, duration: 1 }, `section-${i}`)
              .to(panel.querySelector('.panel-logo'), { opacity: 1, y: 0, duration: 0.4, delay: 0.3 }, `section-${i}`)
              .to(panel.querySelector('.text-content-wrapper'), { opacity: 1, y: 0, duration: 0.4, delay: 0.4 }, `section-${i}`);
          });

          // Arrows reveal
          gsap.fromTo(
            indicatorRef.current,
            { opacity: 0 },
            {
              opacity: 1,
              scrollTrigger: { trigger: pinWrapperRef.current, start: 'top top', end: '+=500', scrub: true },
            }
          );
        },

        // --- MOBILE ANIMATION ---
        "(max-width: 768px)": function () {
          panels.forEach((panel) => {
            gsap.fromTo(panel,
              { opacity: 0.2, y: 50 },
              {
                opacity: 1, y: 0,
                scrollTrigger: { trigger: panel, start: "top 80%", end: "center center", scrub: true }
              });
          });
        }
      });
    }, sectionRef);

    // Forces ScrollTrigger to recalibrate heights after all other homepage images load
    const refreshTimer = setTimeout(() => { ScrollTrigger.refresh(true); }, 750);

    // Observes whole document for shifting sizes to prevent "early animation" bugs
    const sizeObserver = new ResizeObserver(() => {
      ScrollTrigger.refresh(true);
    });
    sizeObserver.observe(document.body);

    return () => {
      clearTimeout(refreshTimer);
      sizeObserver.disconnect();
      ctx.revert();
    };
  }, []);

  return (
    <section ref={sectionRef} className="achievements-root-wrapper">
      <div ref={pinWrapperRef} className="achievements-section">

        <div className="title-wrapper">
          <h2 ref={titleRef} className="achievements-title">Our Achievements</h2>
        </div>

        <div className="achievements-container">
          {achievementData.map((panel) => (
            <div key={panel.id} ref={addToRefs} className="panel" style={{ backgroundImage: `url(${panel.imageUrl})` }}>
              <div className="panel-wrapper">
                <div className="panel-content">
                  <img src={panel.logoUrl} alt={`${panel.title} project logo`} className="panel-logo" aria-hidden="true" />
                  <div className="text-content-wrapper">
                    <h3>{panel.title}</h3>
                    <p>{panel.description}</p>
                    <a href={panel.linkUrl} target="_blank" rel="noopener noreferrer" className="project-link-btn">
                      View Project
                    </a>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div ref={indicatorRef} className="achievements-scroll-down-indicator" role="button" aria-label="Skip to last slide">
          <div className="arrow-container">
            <div className="arrow-down"></div>
            <div className="arrow-down"></div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default AchievementsSection;